import SellerAuth from ".";
import { AUTH_ONBOARDING_SHELL_CLASS } from "./auth-shell";
import verifiedImg from "../../assets/images/verified.png";
import serviceImg from "../../assets/images/service.png";
import moneyImg from "../../assets/images/money.png";
import MaButton from "../../Components/UI/ma-button";
import { Link, useNavigate } from "react-router-dom";
import MaModal from "../../Components/UI/ma-modal";
import { useState } from "react";
import { IconButton } from "@mui/material";
import { IoClose } from "react-icons/io5";
import { FiUser, FiUserPlus } from "react-icons/fi";
import { IoIosArrowRoundForward } from "react-icons/io";
import useAuthFlow from "../../Hooks/use-auth-flow";
import { useSelector } from "react-redux";

function OnboardingOptionCard({
  cardClassName,
  iconBg,
  icon,
  title,
  description,
  action,
}) {
  return (
    <div
      className={`flex flex-col gap-4 rounded-[8px] border border-solid px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${cardClassName}`}
    >
      <div className="flex min-w-0 gap-x-3">
        <div
          className={`grid h-[52px] w-[52px] shrink-0 place-content-center rounded-full sm:h-[60px] sm:w-[60px] ${iconBg}`}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <p className="my-0 text-[14px] font-black text-black sm:text-[15px]">
            {title}
          </p>
          <p className="my-0 mt-1 text-[12px] leading-relaxed text-[#626262]">
            {description}
          </p>
        </div>
      </div>
      <div className="w-full shrink-0 sm:w-auto [&>div]:w-full sm:[&>div]:w-auto">
        {action}
      </div>
    </div>
  );
}

function FeatureRow({ image, alt, title, description }) {
  return (
    <div className="mt-5 flex gap-x-4 first:mt-0 sm:gap-x-5">
      <img
        src={image}
        alt={alt}
        className="h-[44px] w-[44px] shrink-0 sm:h-[50px] sm:w-[50px]"
      />
      <div className="min-w-0">
        <h3 className="my-0 mt-1 text-[16px] font-semibold text-[#3D3D3D] sm:mt-2 sm:text-[18px]">
          {title}
        </h3>
        <p className="mt-1 text-[13px] text-[#626262] sm:text-[14px]">
          {description}
        </p>
      </div>
    </div>
  );
}

const Onboarding = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { triggerAuth } = useAuthFlow();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const navigate = useNavigate();
  const handleLogin = () => {
    if (isAuthenticated) {
      navigate("/seller/create-store");
      return;
    }
    setIsOpen(false);
    triggerAuth("signin");
  };

  return (
    <>
      <SellerAuth>
        <div className={AUTH_ONBOARDING_SHELL_CLASS}>
          <h2 className="playfair my-0 text-[24px] font-bold text-black sm:text-[28px] lg:text-[32px]">
            Sell on Mamaket
          </h2>
          <span className="mt-2 text-[13px] text-[#222222] sm:text-[14px]">
            Let's get you set up to start selling on Mamaket.
          </span>

          <div className="mt-8 lg:mt-10">
            <FeatureRow
              image={verifiedImg}
              alt="Verified"
              title="Get verified"
              description="Verify your identity in seconds with our secure and reliable ID verification service."
            />
            <FeatureRow
              image={serviceImg}
              alt="Service"
              title="List items or services"
              description="Easily add your products/services online and reach a wider audience with just a few clicks."
            />
            <FeatureRow
              image={moneyImg}
              alt="Money"
              title="Get Paid"
              description="Get paid effortlessly! Complete a simple post-sale step and receive payment directly to your bank account."
            />
          </div>

          <div className="mt-8 lg:mt-10">
            <MaButton
              onClick={() => setIsOpen(true)}
              variant={"primary"}
              fullWidth
              title="Get Started"
              sx={{ bgcolor: "#2E1834", borderRadius: "16px" }}
            />
          </div>
        </div>
      </SellerAuth>

      <MaModal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className="box-border w-[calc(100vw-32px)] max-w-[600px] rounded-[16px] bg-white px-4 py-4 sm:w-[600px] sm:px-6">
          <div className="flex items-center justify-end">
            <IconButton onClick={() => setIsOpen(false)}>
              <IoClose />
            </IconButton>
          </div>

          <div>
            <p className="playfair my-0 text-center text-[20px] font-bold sm:text-[22px]">
              Start Selling on Mamaket
            </p>
            <p className="my-0 mt-1 text-center text-[13px] text-[#626262] sm:text-[14px]">
              Let's setup your seller account
            </p>
          </div>

          <p className="text-center text-[13px] sm:text-[14px]">
            Do you already have a mamaket account?
          </p>

          <OnboardingOptionCard
            cardClassName="mt-6 border-[#D7B9DF] bg-[#F5EEF7]"
            iconBg="bg-[#D7B9DF]"
            icon={
              <FiUser className="text-[22px] text-[#2E1834] sm:text-[25px]" />
            }
            title="Existing Mamaket User"
            description="If you already have an account, log in and continue setting up your seller profile."
            action={
              <MaButton
                onClick={handleLogin}
                fullWidth
                sx={{
                  border: "1px solid #2E1834",
                  color: "#2E1834",
                  bgcolor: "transparent",
                  whiteSpace: "nowrap",
                }}
                endIcon={<IoIosArrowRoundForward />}
                title="Continue to login"
                variant={"primary"}
              />
            }
          />

          <OnboardingOptionCard
            cardClassName="mb-5 mt-5 border-[#edf8e7] bg-[#f7fdf8]"
            iconBg="bg-[#edf8e7]"
            icon={
              <FiUserPlus className="text-[22px] text-[#219d5a] sm:text-[25px]" />
            }
            title="New to Mamaket"
            description="Create a new mamaket account and start onboarding as a seller"
            action={
              <Link to="/seller-signup" className="block w-full sm:w-auto">
                <MaButton
                  fullWidth
                  sx={{ bgcolor: "#219d5a", color: "#fff" }}
                  endIcon={<IoIosArrowRoundForward />}
                  title="Create Account"
                  variant={"primary"}
                />
              </Link>
            }
          />
        </div>
      </MaModal>
    </>
  );
};

export default Onboarding;
